// reminder.jsx
import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import {
  Typography,
  Box,
  CssBaseline,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  Chip,
  Modal,
  Grid,
} from '@mui/material';
import { Add, Phone, CheckCircle, Cancel, Notifications, Close } from '@mui/icons-material';
import axios from 'axios';
import { global_link } from '../Components/Globallink';
import WebSocketContext from '../Context/WebSocket';
import modalStyle from '../Components/ModalStyle';

const STATUS_COLORS = {
  pending:   'warning',
  fired:     'info',
  done:      'success',
  cancelled: 'default',
};

const Reminders = () => {
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [data, setData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ remind_at: '', note: '', contact_name: '', contact_number: '' });

  // Incoming "reminder due" popup
  const [duePopup, setDuePopup] = useState(null); // { reminderId, contactName, contactNumber, note }

  const api = axios.create({ baseURL: global_link });
  const authToken = process.env.AUTH_TOKEN;

  // Logged-in user (for owner checks + trigger isolation)
  const myUserId = sessionStorage.getItem('user_id') || localStorage.getItem('user_id') || '';
  const myUserLevel = sessionStorage.getItem('user_level') || localStorage.getItem('user_level') || '';

  // ── Shared context socket — same pattern as Calls.jsx ─────────────────────
  const { socket, user_id: ctxUserId } = useContext(WebSocketContext);

  const fetchDataRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const authyToken = localStorage.getItem(authToken);
      const response = await api.get('/reminder/list', {
        params: {
          status: statusFilter,
          search,
          limit: rowsPerPage,
          offset: page * rowsPerPage,
        },
        headers: { Authorization: `Bearer ${authyToken}` },
      });
      setData(response.data.data);
      setTotalItems(response.data.total);
    } catch (error) {
      console.error('Error fetching reminders:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, page, rowsPerPage, authToken]);

  useEffect(() => { fetchDataRef.current = fetchData; }, [fetchData]);
  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Debounce the search box into `search` ─────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // ── Socket: list refresh nudge + the REMINDER_DUE popup ───────────────────
  // Trigger isolation: only show popup for reminders that belong to this user.
  useEffect(() => {
    if (!socket) return;

    const handleListUpdate = () => fetchDataRef.current?.();

    const handleReminderDue = (payload) => {
      const me = String(ctxUserId || myUserId || sessionStorage.getItem('user_id') || '');
      if (me && payload?.userId && String(payload.userId) !== me) return;
      setDuePopup(payload);
      fetchDataRef.current?.(); // refresh so its status flips to "fired" in the table
    };

    socket.on('connect', handleListUpdate);
    socket.on('NEW_REMINDER_UPDATE', handleListUpdate);
    socket.on('REMINDER_DUE', handleReminderDue);

    return () => {
      socket.off('connect', handleListUpdate);
      socket.off('NEW_REMINDER_UPDATE', handleListUpdate);
      socket.off('REMINDER_DUE', handleReminderDue);
    };
  }, [socket, ctxUserId, myUserId]);

  // ── Create ────────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!form.remind_at) return;
    try {
      const authyToken = localStorage.getItem(authToken);
      const d = new Date(form.remind_at);
      d.setHours(d.getHours() - 1);
      const pad = (n) => String(n).padStart(2, '0');
      const remindAtAdjusted =
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
        `${pad(d.getHours())}:${pad(d.getMinutes())}:00`;

      await api.post('/reminder/add', { ...form, remind_at: remindAtAdjusted }, {
        headers: { Authorization: `Bearer ${authyToken}` },
      });
      setCreateOpen(false);
      setForm({ remind_at: '', note: '', contact_name: '', contact_number: '' });
      fetchData();
    } catch (error) {
      console.error('Error creating reminder:', error);
    }
  };

  // ── Who may complete / cancel ─────────────────────────────────────────────
  // Owner: always. Supervisor / admin / superadmin: only if remind_at is ≥1 day past.
  const canClose = (row) => {
    if (!row) return false;
    const me = String(ctxUserId || myUserId || '');
    if (me && String(row.user_id) === me) return true;

    const level = (myUserLevel || '').toLowerCase();
    const elevated = level === 'admin' || level === 'supervisor' || level === 'superadmin';
    if (!elevated) return false;

    const remindAt = new Date(row.remind_at);
    if (Number.isNaN(remindAt.getTime())) return false;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 1);
    return remindAt <= cutoff;
  };

  // ── Complete / Cancel ─────────────────────────────────────────────────────
  const handleComplete = async (id) => {
    try {
      const authyToken = localStorage.getItem(authToken);
      await api.patch(`/reminder/complete/${id}`, {}, { headers: { Authorization: `Bearer ${authyToken}` } });
      fetchData();
    } catch (error) {
      console.error('Error completing reminder:', error);
    }
  };

  const handleCancel = async (id) => {
    try {
      const authyToken = localStorage.getItem(authToken);
      await api.patch(`/reminder/cancel/${id}`, {}, { headers: { Authorization: `Bearer ${authyToken}` } });
      fetchData();
    } catch (error) {
      console.error('Error cancelling reminder:', error);
    }
  };

  // ── Call from popup — reuse DialBar copy-to-dialpad event ──────────────────
  const callNow = (number) => {
    if (number) window.dispatchEvent(new CustomEvent('copyPhoneNumber', { detail: number }));
    setDuePopup(null);
  };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fmt = (dt) => {
    if (!dt) return '';
    const d = new Date(dt);
    return isNaN(d) ? dt : d.toLocaleString();
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <CssBaseline />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '15px' }}>
        <Typography variant="h5">Reminders</Typography>
      </Box>

      <Box sx={{ bgcolor: 'background.paper', boxShadow: 'none', border: `0.1px solid rgba(207, 211, 212, 0.2)`, borderRadius: '5px' }}>
        {/* Toolbar: Search (~5) | Status (~4) | Add (~3) — button shorter than filters */}
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5} md={5}>
              <TextField
                variant="outlined"
                label="Search"
                placeholder="Name, number, note, or owner"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                fullWidth
                size="medium"
              />
            </Grid>

            <Grid item xs={12} sm={4} md={4}>
              <FormControl variant="outlined" fullWidth size="medium">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="fired">Due</MenuItem>
                  <MenuItem value="done">Done</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3} md={3}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setCreateOpen(true)}
                fullWidth
                sx={{
                  height: 56, // align with outlined inputs
                  whiteSpace: 'nowrap',
                  px: 1.5,
                }}
              >
                Add reminder
              </Button>
            </Grid>
          </Grid>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'background.table', width: '100%' }}>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Number</TableCell>
                <TableCell>Note</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} align="center">Loading...</TableCell></TableRow>
              ) : data.length > 0 ? (
                data.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{fmt(row.remind_at)}</TableCell>
                    <TableCell>{row.owner_name || '—'}</TableCell>
                    <TableCell>{row.contact_name || '—'}</TableCell>
                    <TableCell>{row.contact_number || '—'}</TableCell>
                    <TableCell>{row.note || '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.status} color={STATUS_COLORS[row.status] || 'default'} />
                    </TableCell>
                    <TableCell align="center">
                      {row.contact_number && (
                        <Button variant="contained" color="info" sx={{ padding: '1px', margin: '3px', minWidth: 0 }} onClick={() => callNow(row.contact_number)}>
                          <Phone sx={{ fontSize: '1.3rem', margin: '4px' }} />
                        </Button>
                      )}
                      {(row.status === 'pending' || row.status === 'fired') && canClose(row) && (
                        <>
                          <Button variant="contained" color="success" sx={{ padding: '4px', margin: '3px', minWidth: 0 }} onClick={() => handleComplete(row.id)}>
                            <CheckCircle sx={{ fontSize: '1.3rem' }} />
                          </Button>
                          <Button variant="contained" color="error" sx={{ padding: '4px', margin: '3px', minWidth: 0 }} onClick={() => handleCancel(row.id)}>
                            <Cancel sx={{ fontSize: '1.3rem' }} />
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow><TableCell colSpan={7} align="center">No reminders</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalItems}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 50, 100]}
          labelRowsPerPage="Rows per page"
        />
      </Box>

      {/* ── Create modal using ModalStyle ── */}
      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box sx={modalStyle}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <h3 id="modal-title">New Reminder</h3>
            <Box display="flex" justifyContent="right">
              <Close onClick={() => setCreateOpen(false)} sx={{ cursor: 'pointer' }} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Remind at"
              type="datetime-local"
              value={form.remind_at}
              onChange={e => setForm({ ...form, remind_at: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Contact name"
              value={form.contact_name}
              onChange={e => setForm({ ...form, contact_name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Contact number"
              value={form.contact_number}
              onChange={e => setForm({ ...form, contact_number: e.target.value })}
              fullWidth
            />
            <TextField
              label="Note"
              value={form.note}
              onChange={e => setForm({ ...form, note: e.target.value })}
              multiline
              minRows={2}
              fullWidth
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleCreate} disabled={!form.remind_at}>Save</Button>
          </Box>
        </Box>
      </Modal>

      {/* ── Reminder-due popup using ModalStyle ── */}
      <Modal
        open={Boolean(duePopup)}
        onClose={() => setDuePopup(null)}
        aria-labelledby="due-modal-title"
        aria-describedby="due-modal-description"
      >
        <Box sx={{ ...modalStyle, maxWidth: '500px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <h3 id="due-modal-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Notifications color="warning" /> Reminder Due
            </h3>
            <Box display="flex" justifyContent="right">
              <Close onClick={() => setDuePopup(null)} sx={{ cursor: 'pointer' }} />
            </Box>
          </Box>
          <Box sx={{ mt: 2 }}>
            {duePopup?.contactName && (
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {duePopup.contactName}
              </Typography>
            )}
            {duePopup?.contactNumber && (
              <Typography variant="body1" sx={{ mt: 1 }}>
                {duePopup.contactNumber}
              </Typography>
            )}
            {duePopup?.note && (
              <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                {duePopup.note}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <Button onClick={() => setDuePopup(null)}>Dismiss</Button>
            {duePopup?.reminderId && (
              <Button color="success" onClick={() => { handleComplete(duePopup.reminderId); setDuePopup(null); }}>
                Mark done
              </Button>
            )}
            {duePopup?.contactNumber && (
              <Button variant="contained" startIcon={<Phone />} onClick={() => callNow(duePopup.contactNumber)}>
                Call now
              </Button>
            )}
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default Reminders;